import React from "react";
import { motion } from "framer-motion";
import aUP5_creative from "./../../../assets/Creative.svg";
import aUP5_lineTitle from "./../../../assets/aUP5Vector1.svg";
import aUP5_image from "./../../../assets/aUE_image_7.svg";

export const TeamSection: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  return (
    <div className="w-full min-h-screen bg-black flex flex-col items-center justify-center px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8 sm:py-20 overflow-hidden">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full h-full flex flex-col items-center justify-center max-w-7xl mx-auto"
      >
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5, ease: "backOut" }}
          className="flex flex-col items-center justify-center gap-3 sm:gap-4 md:gap-6 lg:gap-8 "
        >
          <motion.h1
            initial={{ opacity: 0, letterSpacing: "1rem" }}
            animate={{ opacity: 1, letterSpacing: "0.1rem" }}
            transition={{ duration: 1.2, delay: 0.8 }}
            className="text-white text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-bold font-montserrat text-center leading-tight"
          >
            Equipo
          </motion.h1>
          <motion.img
            draggable={false}
            initial={{ opacity: 0, scale: 0, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 1, ease: "backOut" }}
            whileHover={{
              scale: 1.2,
              rotate: 10,
              transition: { duration: 0.3 },
            }}
            src={aUP5_lineTitle}
            className="w-12 sm:w-16 md:w-20 lg:w-24 xl:w-28 h-auto cursor-pointer mt-0 lg:-mt-4"
            alt="Decorative line"
          />
        </motion.div>

        {/* Main Content */}
        <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8 md:gap-10 lg:gap-12 xl:gap-16">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -150, rotateY: -45 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{
              duration: 1.2,
              ease: "backOut",
              type: "spring",
              bounce: 0.3,
            }}
            className="flex flex-col items-center lg:items-start justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-10 flex-1 order-2 lg:order-1"
          >
            {/* Decorative Images */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 md:gap-6 lg:gap-8 mb-4 sm:mb-6 md:mb-8"
            >
              <motion.img
                draggable={false}
                initial={{ opacity: 0, rotate: 90, scale: 0 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                transition={{
                  duration: 0.8,
                  delay: 1.6,
                  type: "spring",
                  bounce: 0.6,
                }}
                whileHover={{
                  scale: 1.1,
                  y: -5,
                  transition: { duration: 0.3 },
                }}
                src={aUP5_creative}
                className="w-24 sm:w-32 md:w-40 lg:w-48 xl:w-56 h-auto cursor-pointer"
                alt="Creative badge"
              />
            </motion.div>

            {/* Main Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 50, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, delay: 1.8, ease: "backOut" }}
              className="text-white text-xl sm:text-xl md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-bold font-montserrat text-center lg:text-left leading-tight"
            >
              <motion.span
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 2 }}
                className="inline-block"
              >
                SOMOS EL PODER
              </motion.span>
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>
              <motion.span
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 2.2 }}
                className="inline-block"
              >
                DE LA UNIÓN
              </motion.span>
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.8, delay: 2.4 }}
              className="text-white text-sm sm:text-sm md:text-base lg:text-lg xl:text-xl font-normal font-montserrat text-center lg:text-left max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl leading-relaxed"
            >
              Somos creadores de soluciones inusuales, efectivas y de alto
              impacto, con talento comprometido en la generación de valor para
              los clientes capaz de inspirar un cambio en las personas.
            </motion.p>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 150, scale: 0.5, rotateY: 45 }}
            animate={{ opacity: 1, x: 0, scale: 1, rotateY: 0 }}
            transition={{
              duration: 1.2,
              ease: "backOut",
              type: "spring",
              bounce: 0.3,
            }}
            className="flex-1 flex items-center justify-center lg:justify-end order-1 lg:order-2 mb-6 sm:mb-8 lg:mb-0"
          >
            <motion.img
              draggable={false}
              initial={{ filter: "blur(8px)" }}
              animate={{ filter: "blur(0px)" }}
              transition={{ duration: 1, delay: 2.6 }}
              whileHover={{
                scale: 1.05,
                rotateY: 5,
                transition: { duration: 0.3 },
              }}
              src={aUP5_image}
              className="w-44 sm:w-60 md:w-80 lg:w-96 xl:w-[26rem] 2xl:w-[32rem] h-auto object-contain drop-shadow-lg"
              alt="Team illustration"
            />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default TeamSection;
